/**
 * Generic Object Pool implementation to reduce GC pressure
 *
 * Pattern: Object Pool
 * Purpose: Reuse objects instead of creating new ones
 */

export interface PoolStats {
  available: number;
  inUse: number;
  total: number;
  created: number;
  reused: number;
}

export class ObjectPool<T> {
  private available: T[] = [];
  private inUse = new Set<T>();
  private stats = {
    created: 0,
    reused: 0,
  };

  constructor(
    private factory: () => T,
    private reset: (obj: T) => void,
    private initialSize = 10
  ) {
    // Pre-allocate initial objects
    for (let i = 0; i < initialSize; i++) {
      this.available.push(factory());
      this.stats.created++;
    }
  }

  /**
   * Get an object from the pool
   */
  acquire(): T {
    let obj = this.available.pop();

    if (!obj) {
      obj = this.factory();
      this.stats.created++;
    } else {
      this.stats.reused++;
    }

    this.inUse.add(obj);
    return obj;
  }

  /**
   * Return an object to the pool
   */
  release(obj: T): void {
    if (!this.inUse.has(obj)) {
      console.warn('Attempting to release object not in use');
      return;
    }

    this.reset(obj);
    this.inUse.delete(obj);
    this.available.push(obj);
  }

  /**
   * Release all objects back to the pool
   */
  releaseAll(): void {
    this.inUse.forEach(obj => {
      this.reset(obj);
      this.available.push(obj);
    });
    this.inUse.clear();
  }

  /**
   * Clear the pool entirely
   */
  clear(): void {
    this.available = [];
    this.inUse.clear();
    this.stats = { created: 0, reused: 0 };
  }

  /**
   * Get pool statistics
   */
  getStats(): PoolStats {
    return {
      available: this.available.length,
      inUse: this.inUse.size,
      total: this.available.length + this.inUse.size,
      created: this.stats.created,
      reused: this.stats.reused,
    };
  }

  /**
   * Get reuse efficiency (0-1, higher is better)
   */
  getReuseEfficiency(): number {
    const total = this.stats.created + this.stats.reused;
    return total > 0 ? this.stats.reused / total : 0;
  }
}
